/**
 * Veda Agro — branded SO/PO PDF download helper.
 *
 * Deploy target: frontend/src/pages/veda-agro/shared/orderPdf.ts
 * Calls the server-rendered letterhead PDF (GET /veda-agro/orders/:type/:id/pdf,
 * puppeteer + Veda template) and saves it as <formal-number>.pdf. Companion to
 * printOrder.ts (which stays as the quick browser-print path).
 */

import { api } from '@/utils/api';

/**
 * Open the branded PDF inline in a new tab (for the Print buttons — user
 * prints from the browser's PDF viewer, so Print and PDF show the SAME
 * branded document). Window is opened synchronously on the click gesture
 * to dodge popup blockers, then pointed at the blob once fetched.
 */
export async function openOrderPdf(type: 'so' | 'po', id: string): Promise<void> {
  const w = window.open('', '_blank');
  try {
    const res = await api.get(`/veda-agro/orders/${type}/${id}/pdf`, {
      responseType: 'blob',
      timeout: 60_000,
    });
    const url = URL.createObjectURL(res.data as Blob);
    if (w) w.location.href = url;
    else window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    if (w) w.close();
    throw err;
  }
}

export async function downloadOrderPdf(
  type: 'so' | 'po',
  id: string,
  fallbackName: string,
): Promise<void> {
  const res = await api.get(`/veda-agro/orders/${type}/${id}/pdf`, {
    responseType: 'blob',
    // PDF render (puppeteer cold-start) can exceed the default axios timeout.
    timeout: 60_000,
  });
  const blob = res.data as Blob;
  const name = `${fallbackName.replace(/[^\w.-]+/g, '-')}.pdf`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5_000);
}
